import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { cn } from "@/lib/utils";
import { useSession } from "@/state/session";
import { toast } from "sonner";
import { api, unwrapEnvelope } from "@/lib/api";
import {
  LayoutDashboard, Compass, ScanSearch, Bookmark, Boxes, Package, LineChart,
  ReceiptText, Settings, ChevronDown, Radar, PanelLeftClose, PanelLeftOpen, Sliders,
  ChevronsUpDown, User, Building2, CreditCard, Users, Bell, LifeBuoy, Command, LogOut, Map,
} from "lucide-react";
import { STORAGE_KEYS, loadJSON, saveJSON, DEFAULT_PINNED, DESTINATION_BY_ID } from "@/lib/prefs";
import { ManageShortcutsModal } from "./ManageShortcutsModal";
import { StorefrontHunterIcon } from "@/components/storefront-hunter/icons";
import { useSavedLeadsCount } from "@/lib/leads/useSavedLeads";

const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, to: "/app/dashboard" },
  { label: "Product Research", icon: Compass, children: [
    { to: "/app/research/quick-winner", label: "Quick Winner" },
    { to: "/app/research/auto-sourcing", label: "Auto Sourcing" },
    { to: "/app/research/finder", label: "Product Finder" },
    { to: "/app/research/storefront-hunter", label: "Storefront Hunter", icon: StorefrontHunterIcon, testid: "nav-storefront-hunter" },
    { to: "/app/research/top", label: "Top-Selling Products" },
    { to: "/app/research/reverse", label: "Reverse Sourcing" },
    { to: "/app/research/saved", label: "Saved Searches" },
  ]},
  { label: "Product Analyzer", icon: ScanSearch, to: "/app/analyzer" },
  { label: "Marketplace Intelligence", icon: Map, children: [
    { to: "/app/marketplace/seller-map", label: "Seller Map" },
    { to: "/app/marketplace/distributor-map", label: "Distributor Map" },
  ]},
  { label: "Leads", icon: Bookmark, children: [
    // Live count from the same saved-lead store every card uses.
    { to: "/app/leads", label: "Saved Leads", badgeKey: "savedLeads" },
    { to: "/app/leads/watchlist", label: "Watchlist" },
    { to: "/app/leads/collections", label: "Collections" },
    { to: "/app/leads/alerts", label: "Alerts" },
  ]},
  { label: "Sourcing", icon: Boxes, children: [
    { to: "/app/sourcing/oa", label: "Online Arbitrage" },
    { to: "/app/sourcing/scanner", label: "Store Scanner" },
    { to: "/app/sourcing/catalogs", label: "Wholesale Catalogs" },
    { to: "/app/sourcing/suppliers", label: "Suppliers" },
  ]},
  { label: "Inventory", icon: Package, children: [
    { to: "/app/inventory", label: "Inventory Overview" },
    { to: "/app/inventory/replenishment", label: "Replenishment" },
    { to: "/app/inventory/wfs", label: "WFS Shipments" },
    { to: "/app/inventory/po", label: "Purchase Orders" },
  ]},
  { label: "Sales & Profit", icon: LineChart, children: [
    { to: "/app/sales", label: "Sales" },
    { to: "/app/sales/profit", label: "Profit Dashboard" },
    { to: "/app/sales/expenses", label: "Expenses" },
    { to: "/app/sales/ppc", label: "PPC" },
    { to: "/app/sales/payouts", label: "Payouts" },
  ]},
  { label: "Receipts", icon: ReceiptText, children: [
    { to: "/app/receipts", label: "Upload" },
    { to: "/app/receipts/scan", label: "Scan" },
    { to: "/app/receipts/transactions", label: "Transactions" },
    { to: "/app/receipts/reconciliation", label: "Reconciliation" },
  ]},
  { label: "Settings", icon: Settings, to: "/app/settings" },
];

export const FLAT_NAV = NAV.flatMap((n) => n.children ? n.children.map((c) => ({ ...c, group: n.label })) : [{ to: n.to, label: n.label, group: n.label }]);

/**
 * Badge value for a nav item. Live counts come from the shared saved-lead store;
 * a static `badge` number would be a fixture masquerading as data.
 */
export function badgeValueFor(item, savedCount) {
  if (!item) return null;
  if (item.badgeKey === "savedLeads") return savedCount > 0 ? savedCount : null;
  return item.badge ?? null;
}

function Leaf({ item, collapsed }) {
  const Icon = item.icon;
  // Subscribes to the shared store (no fetch here — the Sidebar root hydrates
  // once), so the badge updates the moment any view saves or unsaves a lead.
  const savedCount = useSavedLeadsCount({ hydrate: false });
  const badgeFor = (it) => badgeValueFor(it, savedCount);
  return (
    <NavLink to={item.to} data-testid={item.testid || `nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) => cn("group relative flex items-center gap-2.5 h-9 rounded-ctrl px-2.5 text-compact transition-colors",
        collapsed ? "mx-2 justify-center" : "ml-6 mr-3",
        isActive ? "bg-navy-800 text-ink-inverse" : "text-ink-inverse/60 hover:text-ink-inverse hover:bg-navy-800/50")}
      title={collapsed ? item.label : undefined}>
      {({ isActive }) => (<>
        {isActive && !collapsed && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r bg-emerald-500 ez-active-bar" />}
        {Icon
          ? <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-emerald-500" : "text-ink-inverse/55")} />
          : (!collapsed && <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", isActive ? "bg-emerald-500" : "bg-ink-inverse/25")} />)}
        {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
        {badgeFor(item) ? !collapsed && (
          <span className="text-micro font-mono bg-navy-850 rounded px-1.5">{badgeFor(item)}</span>
        ) : null}
      </>)}
    </NavLink>
  );
}

function Group({ item, collapsed, open, onToggle }) {
  const loc = useLocation();
  const Icon = item.icon;
  const childActive = item.children?.some((c) => loc.pathname === c.to);

  if (item.to) {
    return (
      <NavLink to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        className={({ isActive }) => cn("group relative flex items-center gap-2.5 h-10 rounded-ctrl px-2.5 mx-3 text-compact transition-colors",
          collapsed && "justify-center mx-2",
          isActive ? "bg-navy-800 text-ink-inverse" : "text-ink-inverse/70 hover:text-ink-inverse hover:bg-navy-800/50")}
        title={collapsed ? item.label : undefined}>
        {({ isActive }) => (<>
          {isActive && !collapsed && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r bg-emerald-500 ez-active-bar" />}
          <Icon className={cn("h-5 w-5 shrink-0", isActive && "text-emerald-500")} />
          {!collapsed && <span className="flex-1 truncate font-medium">{item.label}</span>}
        </>)}
      </NavLink>
    );
  }

  return (
    <div>
      <button onClick={onToggle} aria-expanded={open} title={collapsed ? item.label : undefined}
        className={cn("w-full flex items-center gap-2.5 h-10 rounded-ctrl px-2.5 text-compact transition-colors",
          collapsed ? "justify-center mx-2 w-[calc(100%-16px)]" : "mx-3 w-[calc(100%-24px)]",
          childActive ? "text-ink-inverse" : "text-ink-inverse/70 hover:text-ink-inverse hover:bg-navy-800/50")}>
        <Icon className={cn("h-5 w-5 shrink-0", childActive && "text-emerald-500")} />
        {!collapsed && <span className="flex-1 text-left truncate font-medium">{item.label}</span>}
        {!collapsed && <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />}
      </button>
      {open && !collapsed && <div className="mt-0.5 space-y-0.5">{item.children.map((c) => <Leaf key={c.to} item={c} collapsed={collapsed} />)}</div>}
    </div>
  );
}

function AccountMenu({ collapsed }) {
  const { session, signOut } = useSession();
  const nav = useNavigate();
  const displayName = session?.name || session?.email?.split("@")[0] || "Account";
  const initials = displayName
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const workspaceLine = `${session?.org || "Ezisell Workspace"} · ${session?.plan || "Growth"}`;
  const items = [
    { label: "View profile", icon: User, act: () => nav("/app/profile") },
    { label: "Workspace settings", icon: Building2, act: () => nav("/app/settings") },
    { label: "Billing & plan", icon: CreditCard, act: () => nav("/app/settings?tab=billing") },
    { label: "Team members", icon: Users, act: () => nav("/app/team") },
    { label: "Notifications", icon: Bell, act: () => toast("Notifications (demo)") },
    { label: "Help & support", icon: LifeBuoy, act: () => toast("Help (demo)") },
    { label: "Keyboard shortcuts", icon: Command, act: () => toast("⌘K, G+D, G+A … (demo)") },
  ];
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button data-testid="account-trigger" title={collapsed ? `${displayName} · ${workspaceLine}` : undefined}
          className={cn("w-full flex items-center gap-2.5 rounded-ctrl hover:bg-navy-800 p-1.5", collapsed && "justify-center")}>
          <div className="h-8 w-8 rounded-full bg-emerald-600 text-white grid place-items-center text-micro font-semibold shrink-0">{initials}</div>
          {!collapsed && <div className="min-w-0 flex-1 text-left"><div className="text-compact text-ink-inverse truncate">{displayName}</div><div className="text-micro text-ink-inverse/50 truncate">{workspaceLine}</div></div>}
          {!collapsed && <ChevronsUpDown className="h-4 w-4 text-ink-inverse/40 shrink-0" />}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content side="top" align="start" sideOffset={8} data-testid="account-menu"
          className="z-[80] w-60 bg-surface-0 border border-edge-subtle rounded-panel shadow-menu p-1.5 ez-rise-in">
          <div className="px-2 py-2 mb-1 border-b border-edge-subtle">
            <div className="text-compact font-medium text-ink-primary">{displayName}</div>
            <div className="text-micro text-ink-muted">{workspaceLine}</div>
          </div>
          <DropdownMenu.Item onSelect={() => toast("Switched workspace (demo)")} className="flex items-center gap-2 px-2 h-8 rounded-ctrl text-compact hover:bg-surface-2 outline-none cursor-pointer">
            <Building2 className="h-4 w-4 text-ink-muted" /> Ezisell Workspace <span className="ml-auto text-micro text-ink-muted">Switch</span>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="h-px bg-edge-subtle my-1" />
          {items.map((it) => { const Icon = it.icon; return (
            <DropdownMenu.Item key={it.label} onSelect={it.act} className="flex items-center gap-2 px-2 h-8 rounded-ctrl text-compact hover:bg-surface-2 outline-none cursor-pointer">
              <Icon className="h-4 w-4 text-ink-muted" /> {it.label}
            </DropdownMenu.Item>
          ); })}
          <DropdownMenu.Separator className="h-px bg-edge-subtle my-1" />
          <DropdownMenu.Item data-testid="account-logout" onSelect={() => { signOut(); nav("/signin"); }} className="flex items-center gap-2 px-2 h-8 rounded-ctrl text-compact text-red-600 hover:bg-red-100 outline-none cursor-pointer">
            <LogOut className="h-4 w-4" /> Log out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function sectionWithActiveChild(pathname) {
  return NAV.find((n) => n.children?.some((c) => c.to === pathname))?.label ?? null;
}

export function Sidebar({ collapsed, onToggle }) {
  const loc = useLocation();
  // Hydrate the saved-lead count once per app shell load (never per row).
  useSavedLeadsCount();
  const [pinned, setPinned] = useState(() => loadJSON(STORAGE_KEYS.pinnedShortcuts, DEFAULT_PINNED));
  const [manage, setManage] = useState(false);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    api
      .usage()
      .then((res) => setUsage(unwrapEnvelope(res)))
      .catch(() => setUsage({ available: false, message: "Usage unavailable" }));
  }, []);
  // Accordion: at most one collapsible section open (null = all collapsed)
  const [openSection, setOpenSection] = useState(() => sectionWithActiveChild(loc.pathname));
  const savePinned = (ids) => { setPinned(ids); saveJSON(STORAGE_KEYS.pinnedShortcuts, ids); };
  const handleToggle = () => {
    onToggle();
    // Let dependent canvases (e.g. Leaflet Seller Map) reflow into the new width.
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    window.setTimeout(() => window.dispatchEvent(new Event("resize")), 230);
  };

  React.useEffect(() => {
    const active = sectionWithActiveChild(loc.pathname);
    if (active) setOpenSection(active);
  }, [loc.pathname]);

  const toggleSection = (label) => {
    setOpenSection((current) => (current === label ? null : label));
  };

  return (
    <aside data-testid="sidebar" className={cn("ez-sidebar fixed left-0 top-0 bottom-0 z-30 bg-navy-900 flex flex-col", collapsed ? "w-[72px]" : "w-[240px]")}>
      <div className="h-14 flex items-center gap-2.5 px-4 border-b border-navy-800 shrink-0">
        <div className="h-8 w-8 rounded-panel bg-emerald-600 flex items-center justify-center shrink-0"><Radar className="h-5 w-5 text-white" /></div>
        {!collapsed && <div className="min-w-0 ez-sidebar-label"><div className="text-ink-inverse font-semibold leading-tight">Ezisell</div><div className="text-micro text-ink-inverse/50 truncate">Walmart research</div></div>}
      </div>

      <nav className="flex-1 overflow-y-auto ez-scroll py-1.5 space-y-0.5">
        {NAV.slice(0, 1).map((n) => (
          <Group key={n.label} item={n} collapsed={collapsed}
            open={openSection === n.label}
            onToggle={() => toggleSection(n.label)} />
        ))}

        {/* Shortcuts */}
        <div className="pt-2 mt-1 border-t border-navy-800">
          {!collapsed && (
            <div className="flex items-center justify-between px-4 h-7">
              <span className="text-micro font-mono uppercase tracking-wide text-ink-inverse/40">Shortcuts</span>
              <button data-testid="manage-shortcuts-btn" onClick={() => setManage(true)} title="Manage shortcuts" className="h-6 w-6 grid place-items-center rounded text-ink-inverse/50 hover:text-ink-inverse hover:bg-navy-800"><Sliders className="h-3.5 w-3.5" /></button>
            </div>
          )}
          <div className="space-y-0.5 mt-0.5">
            {pinned.map((id) => { const dst = DESTINATION_BY_ID[id]; if (!dst) return null; return <Leaf key={id} item={{ to: dst.to, label: dst.label, icon: dst.icon, testid: `shortcut-${id}` }} collapsed={collapsed} />; })}
          </div>
          {collapsed && (
            <button data-testid="manage-shortcuts-btn-collapsed" onClick={() => setManage(true)} title="Manage shortcuts" className="mx-2 mt-0.5 h-9 w-[calc(100%-16px)] grid place-items-center rounded-ctrl text-ink-inverse/50 hover:text-ink-inverse hover:bg-navy-800"><Sliders className="h-4 w-4" /></button>
          )}
        </div>

        {NAV.slice(1).map((n) => (
          <Group key={n.label} item={n} collapsed={collapsed}
            open={openSection === n.label}
            onToggle={() => toggleSection(n.label)} />
        ))}
      </nav>

      <div className="border-t border-navy-800 p-2.5 space-y-2 shrink-0">
        {!collapsed && (
          <div className="rounded-ctrl bg-navy-850 p-2.5" data-testid="usage-section">
            {usage?.available ? (
              <>
                <div className="flex items-center justify-between text-micro text-ink-inverse/60 mb-1">
                  <span>Analyses used</span>
                  <span className="font-mono text-ink-inverse/80">
                    {Number(usage.analyses_used || 0).toLocaleString()} /{" "}
                    {usage.analyses_limit == null
                      ? "Unlimited"
                      : Number(usage.analyses_limit).toLocaleString()}
                  </span>
                </div>
                {usage.analyses_limit != null && (
                  <div className="h-1.5 rounded bg-navy-800 overflow-hidden">
                    <div
                      className="h-full bg-emerald-500"
                      style={{
                        width: `${Math.min(
                          100,
                          (Number(usage.analyses_used || 0) /
                            Number(usage.analyses_limit)) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <p className="text-micro text-ink-inverse/60">
                {usage?.message || "Usage unavailable"}
              </p>
            )}
          </div>
        )}
        <button
          type="button"
          data-testid="sidebar-toggle"
          onClick={handleToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={cn(
            "flex items-center gap-2.5 min-h-[42px] rounded-ctrl border border-transparent text-ink-inverse/60 hover:text-ink-inverse hover:bg-navy-800 transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-emerald-500/40",
            collapsed ? "w-11 h-11 mx-auto justify-center px-0" : "w-full justify-start px-3"
          )}>
          {collapsed
            ? <PanelLeftOpen className="h-[18px] w-[18px] shrink-0" />
            : <><PanelLeftClose className="h-[18px] w-[18px] shrink-0" /><span className="text-compact">Collapse sidebar</span></>}
        </button>
        <AccountMenu collapsed={collapsed} />
      </div>

      <ManageShortcutsModal open={manage} onOpenChange={setManage} pinned={pinned} onSave={savePinned} />
    </aside>
  );
}
